import { DataTableExpandableCell, DataTableExpandableHeader } from '@optiaxiom/react/unstable';
import { createColumnHelper } from '@tanstack/react-table';

import {
  ChatStatusCell,
  EnabledInChatHeader,
  HeaderWithTooltip,
  NameCell,
  StatusCell,
} from './cells';
import { ToolsTableRow } from './types';

const columnHelper = createColumnHelper<ToolsTableRow>();

type ColumnsProps = {
  enabledToolCount: number;
  isUpdating: boolean;
  updatingToolId: string | null | undefined;
  isUpdatingEnabledInPrimaryAgent: boolean;
  updatingToolIdEnabledInPrimaryAgent: string | null | undefined;
  onToggleActive: (toolId: string, isDisabled: boolean) => void;
  onToggleEnabledInPrimaryAgent: (toolName: string, isEnabledInPrimaryAgent: boolean) => void;
  onBulkToggle: (serviceId: string, functionIds: string[], action: 'enable' | 'disable') => void;
  isBulkToggling: boolean;
  onBulkToggleChatEnabled: (toolNames: string[], action: 'enable' | 'disable') => void;
  isBulkTogglingChat: boolean;
};

const createColumns = ({
  enabledToolCount,
  isUpdating,
  updatingToolId,
  isUpdatingEnabledInPrimaryAgent,
  updatingToolIdEnabledInPrimaryAgent,
  onToggleActive,
  onToggleEnabledInPrimaryAgent,
  onBulkToggle,
  isBulkToggling,
  onBulkToggleChatEnabled,
  isBulkTogglingChat,
}: ColumnsProps) => [
  columnHelper.accessor('name', {
    header: () => <DataTableExpandableHeader>Name</DataTableExpandableHeader>,
    cell: props => (
      <DataTableExpandableCell {...props}>
        <NameCell toolRow={props.row.original} />
      </DataTableExpandableCell>
    ),
    size: 400,
  }),
  columnHelper.accessor('auth_status', {
    header: 'Auth Provider',
    cell: ({ row }) => {
      if (row.original.isRegistry) return null;

      const toolRow = row.original;
      if (toolRow.tool.is_system_function) return 'Opti ID';
      return toolRow.tool.auth_requirements.length === 0
        ? ''
        : toolRow.tool.auth_requirements.map(req => req.provider).join(', ');
    },
  }),
  columnHelper.display({
    id: 'active',
    header: () => (
      <HeaderWithTooltip
        label="Active"
        info="Toggle to activate or deactivate this tool. When active, the tool can be used by agents and, if configured, directly for messages in the main chat. When inactive, the tool is completely unavailable for all use."
      />
    ),
    cell: ({ row }) => (
      <StatusCell
        toolRow={row.original}
        onToggleActive={onToggleActive}
        onBulkToggle={onBulkToggle}
        isUpdating={isUpdating}
        updatingToolId={updatingToolId}
        isUpdatingEnabledInPrimaryAgent={isUpdatingEnabledInPrimaryAgent}
        updatingToolIdEnabledInPrimaryAgent={updatingToolIdEnabledInPrimaryAgent}
        isBulkToggling={isBulkToggling}
      />
    ),
  }),
  columnHelper.display({
    id: 'enabled_in_chat',
    header: () => <EnabledInChatHeader enabledCount={enabledToolCount} />,
    cell: ({ row }) => (
      <ChatStatusCell
        toolRow={row.original}
        onToggleEnabledInPrimaryAgent={onToggleEnabledInPrimaryAgent}
        onBulkToggleChatEnabled={onBulkToggleChatEnabled}
        isBulkToggling={isBulkTogglingChat}
      />
    ),
  }),
];

export default createColumns;
